import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import SEO from "../components/SEO";
import { ArrowRight, Sparkles, Loader2 } from "lucide-react";

/**
 * V2.0.88 — Best Transformations showcase.
 *
 * Marketing funnel page. Renders the top-5 shared designs whose owners
 * started from a hand-drawn sketch (has_upload=true), ranked by
 * `share.impression` count. Each card shows the composed before/after
 * PNG from `/api/share/{slug}/og-image.png` plus a link into the full
 * SharePage.
 *
 * Route: `/best-transformations` and `/showcase` (alias). No auth.
 */
export default function BestTransformationsPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const r = await axios.get(`${API}/discover/best-transformations`, { params: { limit: 5 } });
        if (!cancel) setItems(r.data?.items || []);
      } catch (err) {
        if (!cancel) setError(err?.response?.data?.detail || err?.message || "Could not load showcase");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="best-transformations-page">
      <SEO
        title="Best Sketch-to-3D Transformations"
        description="See the top hand-drawn floor plans real people turned into full 3D homes with Blueprint AI. Real designs, real users, real sketches."
        path="/best-transformations"
      />
      <header className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-mono text-sm text-brass-400 hover:text-brass-300">
          // BLUEPRINT_AI
        </Link>
        <Link
          to="/"
          className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-brass-300"
          data-testid="showcase-home-link"
        >
          Design your own →
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-10">
        <section className="max-w-3xl">
          <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-3">
            // Weekly showcase
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">
            From napkin to <span className="text-brass-400">real 3D home.</span>
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed max-w-2xl">
            These plans started as a hand-drawn sketch, phone photo, or PDF export.
            Blueprint AI turned every one of them into an editable 3D BIM model.
            Here are the top 5 this week.
          </p>
        </section>

        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 font-mono text-xs" data-testid="showcase-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <div className="font-mono text-xs text-rose-400 border border-rose-800/60 bg-rose-950/40 px-4 py-3">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="border border-zinc-800 px-6 py-12 text-center" data-testid="showcase-empty">
            <Sparkles className="w-10 h-10 text-brass-400 mx-auto mb-3" />
            <div className="font-display text-xl text-zinc-100 mb-1">First transformation coming soon</div>
            <div className="text-zinc-500 text-sm max-w-md mx-auto">
              Be the first to share your sketch-to-3D transformation.
              Upload a floor plan sketch in the editor and share the result.
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 bg-brass-600 hover:bg-brass-500 text-white font-mono text-[10px] uppercase tracking-widest px-5 py-2.5"
              data-testid="showcase-empty-cta"
            >
              Start Designing <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {!loading && items.length > 0 && (
          <ol className="space-y-8" data-testid="showcase-list">
            {items.map((item, i) => (
              <ShowcaseCard key={item.share_slug} rank={i + 1} item={item} />
            ))}
          </ol>
        )}

        <section className="border-t border-zinc-800 pt-10 mt-16 text-center">
          <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-3">
            // Your turn
          </div>
          <h2 className="font-display text-2xl sm:text-3xl mb-4">
            Draw. Upload. Watch it become a home.
          </h2>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-brass-600 hover:bg-brass-500 text-white font-mono text-[10px] uppercase tracking-widest px-6 py-3"
            data-testid="showcase-footer-cta"
          >
            Design Yours Free <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

function ShowcaseCard({ rank, item }) {
  return (
    <li
      className="grid md:grid-cols-[80px_1fr] gap-6 border border-zinc-800 hover:border-brass-500/40 p-4 md:p-6 transition-colors"
      data-testid={`showcase-card-${rank}`}
    >
      <div className="font-display text-6xl md:text-7xl text-brass-400/70 leading-none tracking-tight">
        #{rank}
      </div>
      <div className="space-y-4">
        <a
          href={`${API}/share/${item.share_slug}/og-image.png`}
          target="_blank"
          rel="noopener noreferrer"
          className="block relative aspect-[16/9] bg-zinc-900 border border-brass-500/40 overflow-hidden group"
          data-testid={`showcase-og-${rank}`}
        >
          <img
            src={`${API}/share/${item.share_slug}/og-image.png`}
            alt={`Before/after transformation of ${item.name || "a home design"}`}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
            loading="lazy"
          />
        </a>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h3 className="font-display text-xl text-zinc-100">{item.name || "Untitled design"}</h3>
          {item.style && (
            <span className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
              // {item.style}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-3 text-[11px] font-mono text-zinc-500">
          <span>{(item.square_footage || 0).toLocaleString()} sqft</span>
          <span>•</span>
          <span>{item.bedrooms ?? "?"} BR</span>
          <span>•</span>
          <span>{item.bathrooms ?? "?"} BA</span>
          {item.impressions > 0 && (
            <>
              <span>•</span>
              <span>{item.impressions.toLocaleString()} views</span>
            </>
          )}
        </div>
        <Link
          to={item.share_url}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-brass-300 hover:text-brass-200 border-b border-brass-700/60 hover:border-brass-500 pb-0.5"
          data-testid={`showcase-link-${rank}`}
        >
          See full design <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </li>
  );
}
